/** Live speech-to-speech practice — the money side.
 *
 * Three calls, and the order is the design. The server RESERVES a window
 * before it will mint anything, hands back a short-lived credential, and
 * refunds the unused part when the conversation ends. The browser then
 * talks to the vendor directly with that credential — which is why this
 * module never sees an account key, and why `end` matters: skip it and the
 * learner keeps paying for the window they reserved until it expires.
 */
import { request } from "./http";

export interface LiveVoiceOption {
  id: string;
  label: string;
  blurb: string;
  /** Paise per minute of open conversation. The only unit a learner can
   *  act on here — this surface is sold by the minute, not by the call. */
  price_per_minute_minor: number;
}

export interface LiveVoiceStatus {
  configured: boolean;
  voices: LiveVoiceOption[];
  /** Purchased balance, in paise. Live voice is paid from this and never
   *  from the free monthly allowance. */
  balance_minor: number;
  /** How long this learner could talk on each voice right now — bounded by
   *  the session cap AND by what they can afford, which is the same bound
   *  the server's reservation uses. */
  affordable_seconds: Record<string, number>;
  max_session_seconds: number;
  min_session_seconds: number;
}

export interface LiveVoiceSession {
  id: string;
  model: string;
  label: string;
  /** The short-lived credential the browser connects with. Never the
   *  account key, and it stops working when the window closes. */
  client_secret: string;
  expires_at: string;
  max_seconds: number;
  price_per_minute_minor: number;
  reserved_minor: number;
  balance_minor: number;
}

export interface LiveVoiceEnded {
  id: string;
  seconds: number;
  spent_minor: number;
  balance_minor: number;
}

export function liveVoiceStatus(): Promise<LiveVoiceStatus> {
  return request("/exams/speaking/live/status");
}

export function startLiveVoice(body: {
  model?: string | null;
  language?: string;
  level?: string | null;
}): Promise<LiveVoiceSession> {
  return request("/exams/speaking/live/start", { method: "POST", body });
}

export function endLiveVoice(id: string): Promise<LiveVoiceEnded> {
  return request(`/exams/speaking/live/${id}/end`, { method: "POST" });
}
